<template>
  <div class="audio-content-reader" @click="togglePlay">
    <div class="audio-content-reader__btn">
      <PlaybackStatus :isPlaying="isPlaying" :isLoading="isLoading" :color="'white'" :speed="1" />
    </div>
    <time class="audio-content-reader__time">
      <template v-if="isPlaying || currentTime > 0">
        <span class="audio-content-reader__time-current">{{ formattedCurrentTime }}</span>
        <span class="audio-content-reader__time-divider"> / </span>
        <span class="audio-content-reader__time-duration">{{ formattedDuration }}</span>
      </template>
      <template v-else>
        {{ formattedDuration }}
      </template>
    </time>
    <span class="audio-content-reader__label" v-if="!isPlaying">{{ label }}</span>
    <audio
      ref="audioRef"
      :src="src"
      @timeupdate="onTimeUpdate"
      @loadedmetadata="onLoadedMetadata"
      @ended="onEnded"
      @waiting="isLoading = true"
      @canplay="isLoading = false"
    />
  </div>
</template>

<script setup>
  import { ref, computed } from 'vue';
  import { useAudioPlayerState } from '@/Groups/Audioplayer/private/composables/computedValues.js';

  //--------------------------------------------------
  import PlaybackStatus from '@/Groups/Audioplayer/private/ui/PlaybackStatus/PlaybackStatus.vue';

  //--------------------------------------------------
  const props = defineProps({
    src: { type: String, required: true },
    label: { type: String, default: 'Прослушать статью' },
  });

  //--------------------------------------------------
  const audioRef = ref(null);
  const isPlaying = ref(false);
  const isLoading = ref(false);
  const currentTime = ref(0);
  const duration = ref(0);

  //--------------------------------------------------
  const { isPlaying: isMainPlayerPlaying } = useAudioPlayerState();

  //--------------------------------------------------
  //Форматирует секунды в строку MM:SS
  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '00:00';
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  //Возвращает текущее время воспроизведения
  const formattedCurrentTime = computed(() => formatTime(currentTime.value));

  //Возвращает общую длительность
  const formattedDuration = computed(() => formatTime(duration.value));

  //--------------------------------------------------
  //Переключает воспроизведение / паузу
  const togglePlay = async () => {
    if (!audioRef.value) return;
    if (isPlaying.value) {
      audioRef.value.pause();
      isPlaying.value = false;
    } else {
      if (isMainPlayerPlaying.value) {
        await store.dispatch('Audioplayer/pauseAudio');
      }
      audioRef.value.play();
      isPlaying.value = true;
    }
  };

  //Обновляет текущее время при воспроизведении
  const onTimeUpdate = () => { currentTime.value = audioRef.value?.currentTime || 0; };

  //Сохраняет общую длительность после загрузки метаданных
  const onLoadedMetadata = () => { duration.value = audioRef.value?.duration || 0; };

  //Сбрасывает состояние после окончания воспроизведения
  const onEnded = () => { isPlaying.value = false; currentTime.value = 0; };
</script>

<style lang="scss" scoped>
  @import "./styles";
</style>